import { inject, Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import {
    GlobalEmailTypes,
    FactorCombinationType,
    LanguageAvailabilityByProduct,
    AssessmentType
} from '../constants-types';
import { StringUtilities } from '../core-utilities/String.utility';
import { FullSiteLanguage, Language, SupportedSiteLanguage } from '../shared';
import { LanguagesApi } from './LanguagesApi';
import { TranslationCoreService } from './TranslationCore.service';

/*
This is an injectable service that serves as a wrapper around a synchronously
loaded translation files. This is intended to read from objected loaded via
script tags before the application is running.
*/

(window as any).Translations = (window as any).Translations || {};

declare let Translations: any; // Done to expose global variable to this service
type TranslationMode = 'Normal' | 'Hash' | 'Enclose' | 'Key';

@Injectable({
    providedIn: 'root'
})
export class TranslationService {
    readonly #languageApi = inject(LanguagesApi);

    translationsLoaded: Subject<boolean> = new Subject<boolean>();
    specialTranslation: {
        [key: string]: {
            [key: string]: string;
        };
    } = {};

    translationMode: TranslationMode;
    allMissingStrings: any = {};

    private defaultTranslationObject = Translations;

    readonly #uppercaseFormattedSiteLocaleCodes = [
        'en-US',
        'nl-NL',
        'de-DE',
        'it-IT'
    ];

    constructor(
        private trCore: TranslationCoreService,
        private stringUtil: StringUtilities
    ) {}

    get translationModeOn() {
        return this.trCore.translationModeOn;
    }

    get currentLocale() {
        return this.trCore.currentLocale;
    }

    get behavioralLanguages() {
        return this.trCore.behavioralLanguages;
    }

    get cognitiveLanguages() {
        return this.trCore.cognitiveLanguages;
    }

    // Should probably make this a decorator
    // Should also probably AllowAnonymous on language APIs
    get fullSiteLanguages() {
        return this.trCore.fullSiteLanguages;
    }

    get fullSiteLanguagesList() {
        return this.trCore.fullSiteLanguagesList;
    }

    setTranslationMode(mode: TranslationMode) {
        this.trCore.translationMode = mode;
    }

    key(keyToTranslate: string, languageCode?: string): string {
        return this.trCore.key(keyToTranslate, languageCode);
    }

    keyFormatter(keyToTranslate: string, ...params: any[]): string {
        return this.stringUtil.formatString(
            this.trCore.key(keyToTranslate),
            ...params
        );
    }

    keyFormatterLocale(
        keyToTranslate: string,
        languageCode: string,
        ...params: any[]
    ): string {
        return this.stringUtil.formatString(
            this.trCore.key(keyToTranslate, languageCode),
            ...params
        );
    }

    getLanguagesFor(product: LanguageAvailabilityByProduct): Language[] {
        return this.trCore.getLanguagesFor(product);
    }

    getCurrentLanguage(product: LanguageAvailabilityByProduct): Language {
        return this.trCore.getCurrentLanguage(product);
    }

    getLanguagebyAssessmentType(
        product: AssessmentType,
        locale: string
    ): Language {
        return this.trCore.getLanguagebyAssessmentType(product, locale);
    }

    findLanguage(
        locale: string,
        product = LanguageAvailabilityByProduct.BehavioralAssessment
    ): Language {
        return this.trCore.findLanguage(locale, product);
    }

    allowGenderNeutralReports(
        languageCode: string = this.currentLocale
    ): boolean {
        return this.trCore.allowGenderNeutralReports(languageCode);
    }

    // Note that this has some translation difficulties. 'And' may be different word
    // based on what is in list (i.e. 'a' versus 'an')
    // However, this is nicely unit testable!
    formWrittenList(words: string[]) {
        return this.trCore.formWrittenList(words);
    }

    /*
    public setLanguageInformation(languages, cognitiveLanguages, currentLocale) {
        this.languages = languages;
        this.cognitiveLanguages = cognitiveLanguages;
        this.currentLanguage = this.languages.find((lang: Language) => {
            return lang.locale === currentLocale;
        })
    }
*/
    fillInKeys(
        keysToTranslate: string[],
        languageCode: string
    ): Promise<any[]> {
        return this.trCore.fillInKeys(keysToTranslate, languageCode);
    }

    getEmailSubjectMessageOverrides(
        keysToTranslate: string[],
        languageCode: string,
        companyId: string,
        emailType: GlobalEmailTypes
    ) {
        return this.trCore.getEmailSubjectMessageOverrides(
            keysToTranslate,
            languageCode,
            companyId,
            emailType
        );
    }

    // We need to update everyone to use the currentLocaleService but this will work in meantime
    isEnglish() {
        return this.trCore.isEnglish();
    }

    isOnetDataSupported() {
        return this.isEnglish();
    }

    getDefaultBehavioralReportLanguage(): Language {
        return this.trCore.getDefaultBehavioralReportLanguage();
    }

    getDefaultCognitiveReportLanguage(): Language {
        return this.trCore.getDefaultBehavioralReportLanguage();
    }

    getFactorCombinationTranslation(combo: FactorCombinationType): string {
        return this.trCore.getFactorCombinationTranslation(combo);
    }

    notImplementedKey(key: string): string {
        return this.trCore.notImplementedKey(key);
    }

    /**
     * Fetches the supported site languages from the API and formats them.
     * The display names within the supported site languages are already translated.
     */
    fetchSupportedSiteLanguages(): Observable<SupportedSiteLanguage[]> {
        return this.#languageApi.getPI2Languages().pipe(
            map((supportedLanguages) =>
                supportedLanguages
                    .map<SupportedSiteLanguage>((language) => ({
                        localeCode: language.localeCode,
                        displayName: this.#formatLanguageDisplayName(language)
                    }))
                    .sort(this.#sortByDisplayValue)
            )
        );
    }

    /**
     * Sorts the supported site languages by their display value.
     *
     * @param a
     * @param b
     * @private
     */
    #sortByDisplayValue(
        a: SupportedSiteLanguage,
        b: SupportedSiteLanguage
    ): number {
        const aDisplayName = (
            a.displayName.includes('-')
                ? a.displayName.split('-')[1]
                : a.displayName
        ).trim();
        const bDisplayName = (
            b.displayName.includes('-')
                ? b.displayName.split('-')[1]
                : b.displayName
        ).trim();

        return aDisplayName.localeCompare(bDisplayName, undefined, {
            sensitivity: 'case'
        });
    }

    /**
     * Formats the display value of a language for better readability.
     * This method ensures that the locale name is properly cased and
     * handles specific cases for certain languages.
     *
     * Returned values are already translated.
     *
     * @param language
     * @private
     */
    #formatLanguageDisplayName(language: FullSiteLanguage): string {
        let localName = language.localName;

        if (
            !this.#uppercaseFormattedSiteLocaleCodes.includes(
                language.localeCode
            )
        ) {
            localName = localName.charAt(0).toLowerCase() + localName.slice(1);
        }

        switch (language.localeCode) {
            case 'en-US':
                return localName;
            case 'es-ES':
                return 'español (España) - Spanish (Spain)';
            case 'zh-CN':
                return '中文 (简体) - Chinese (China)';
            default:
                return `${localName} - ${this.key(language.translationKey)}`;
        }
    }
}
